/**
 * src/components/spatial/SpatialObjectsCountrySection.tsx
 *
 * Compact "Spatial objects" panel embedded inside the country tooltip.
 * Splits the world into "in this country" + "nearby" lists, both
 * starting at top 3 with an expandable "show more" toggle, plus
 * counts-by-type pills, fly-to / open-editor row actions, and quick
 * "+ Building / + Zone / + Event / …" creation shortcuts.
 *
 * The component is intentionally read-mostly: full CRUD (drawing
 * geometry on the map, editor form) lives in the floating
 * SpatialObjectsAdminOverlay. We hand work off via the `onOpenAdmin`
 * callback so the floating panel can pre-load the right object or
 * pre-fill a template scoped to this country's bbox.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';

import {
  geoLinkSpatialObjectsAdminClient,
  type SpatialObjectAdminRow,
} from '../../services/spatial/geoLinkSpatialObjectsAdminClient';
import type { SpatialObjectType } from '../../services/spatial/geoLinkTypes';
import {
  haversineMeters,
  pointInPolygon,
  rowCenter as proximityRowCenter,
  formatDistance,
} from '../../services/spatial/spatialProximity';

// ─── Types ─────────────────────────────────────────────────────────────

/** Subset of admin-row info we actually use in the row UI. */
type Row = SpatialObjectAdminRow;

/** Bounds the parent (App.tsx) computes for the hovered country. */
export interface CountryScope {
  countryCode: number;
  countryIso2: string | null;
  countryName: string;
  /** [minLng, minLat, maxLng, maxLat]. Optional — if missing we skip the in-country query and only show "nearby". */
  bbox?: [number, number, number, number] | null;
  /** Centre of the country bbox. Used for the "nearby" lookup. */
  centroid?: { lng: number; lat: number } | null;
  /** The country's GeoJSON polygon. When present we point-in-polygon
   *  filter the bbox results so only objects truly inside THIS country
   *  show up (the bbox query leaks neighbours across shared borders). */
  polygon?: { type?: string; coordinates?: unknown } | null;
  /** Live map center — what the operator is currently looking at. Preferred for
   *  quick-add pre-fill because it's far closer to the operator's intent than
   *  the country centroid (creating "Burj Khalifa" while hovering USA used to
   *  drop the object in the middle of Kansas). */
  mapCenter?: { lng: number; lat: number } | null;
  /** Live map zoom — used to decide whether the admin panel should fly the
   *  camera to the new object's centre or trust the operator's existing view. */
  mapZoom?: number | null;
}

/** Structured request the parent fans out to the floating admin panel. */
export interface OpenAdminRequest {
  /** Edit an existing object. */
  objectId?: string;
  /** Start a new object pre-filled with this template id. */
  template?: 'building' | 'floor' | 'zone' | 'event' | 'parcel' | 'trigger';
  /** Coordinates the panel should pre-set as Point geometry. */
  center?: { lng: number; lat: number };
  /** When true, also fly the live map to {@link center}. Defaults to false —
   *  if the operator is already zoomed in we shouldn't yank the camera. */
  flyToCenter?: boolean;
  /** Filter the list to this bbox if it's the panel's only opening hint. */
  bbox?: [number, number, number, number];
}

interface SpatialObjectsCountrySectionProps {
  scope: CountryScope;
  /** Open the floating admin overlay with the request applied. */
  onOpenAdmin: (req: OpenAdminRequest) => void;
  /** Fly the map to a specific lng/lat. Optional `heightMeters` lets the
   *  caller (App.tsx) pre-fit the camera for tall buildings — without it
   *  the Burj Khalifa lands at zoom 17 pitch 0 and you stare straight down
   *  at the spire's tip. */
  onFlyTo: (lng: number, lat: number, heightMeters?: number | null) => void;
  /** Connected wallet, when any. Quick-add and edit affordances are
   *  disabled when this is null — the GeoLink admin write API requires
   *  an authenticated owner_wallet, and the panel itself enforces the
   *  same check before saving. */
  walletAddress?: string | null;
}

// ─── Constants ─────────────────────────────────────────────────────────

const TOP_N           = 3;
/** Radius for the "nearby" query when the user has no bbox info. */
const NEARBY_RADIUS_M = 50_000; // 50 km

const TYPE_META: Record<SpatialObjectType, { label: string; icon: string }> = {
  building:                  { label: 'Building',  icon: '🏢' },
  floor:                     { label: 'Floor',     icon: '🪜' },
  zone:                      { label: 'Zone',      icon: '📐' },
  slice:                     { label: 'Slice',     icon: '✂' },
  object_3d:                 { label: '3D object', icon: '🧊' },
  real_estate:               { label: 'Parcel',    icon: '🏞️' },
  event_object:              { label: 'Event',     icon: '🎉' },
  contract_execution_object: { label: 'Trigger',   icon: '⚡' },
  surface_zone:              { label: 'Surface',   icon: '🖼' },
};

/** Compact "+ X" buttons; the list is small on purpose so the tooltip stays readable. */
const QUICK_TEMPLATES: Array<{ id: OpenAdminRequest['template']; label: string; icon: string }> = [
  { id: 'building', label: '+ Building', icon: '🏢' },
  { id: 'zone',     label: '+ Zone',     icon: '📐' },
  { id: 'event',    label: '+ Event',    icon: '🎉' },
];

// ─── Helpers ───────────────────────────────────────────────────────────

/**
 * Best-effort lng/lat extraction. The admin endpoint returns a
 * pre-computed `centroid: Point` for most rows, but during a fresh
 * create the centroid may temporarily be missing — fall back to the
 * raw geometry's first coordinate.
 */
function rowCenter(row: Row): { lng: number; lat: number } | null {
  const c = row.centroid;
  if (c?.coordinates && Number.isFinite(c.coordinates[0]) && Number.isFinite(c.coordinates[1])) {
    return { lng: c.coordinates[0], lat: c.coordinates[1] };
  }
  const g = row.geometry;
  if (!g) return null;
  const walk = (coords: unknown): [number, number] | null => {
    if (!Array.isArray(coords)) return null;
    if (typeof coords[0] === 'number' && typeof coords[1] === 'number') {
      return [coords[0] as number, coords[1] as number];
    }
    for (const c2 of coords) {
      const r = walk(c2);
      if (r) return r;
    }
    return null;
  };
  const firstCoord = walk((g as { coordinates?: unknown }).coordinates);
  return firstCoord ? { lng: firstCoord[0], lat: firstCoord[1] } : null;
}

function countByType(rows: Row[]): Array<{ type: SpatialObjectType; n: number }> {
  const seen = new Map<SpatialObjectType, number>();
  rows.forEach((r) => {
    const t = (r.objectType ?? 'object_3d') as SpatialObjectType;
    seen.set(t, (seen.get(t) ?? 0) + 1);
  });
  return Array.from(seen.entries())
    .map(([type, n]) => ({ type, n }))
    .sort((a, b) => b.n - a.n);
}

/**
 * De-dupe rows from the in-country list when building the nearby list.
 * Mostly relevant when the country bbox and the nearby radius overlap.
 */
function withoutIds<T extends { id: string }>(rows: T[], skipIds: Set<string>): T[] {
  return rows.filter((r) => !skipIds.has(r.id));
}

// ─── Inline styles (kept here so the section is self-contained and
// doesn't need a CSS sidecar imported into App.tsx) ─────────────────────

const STYLE_SECTION: React.CSSProperties = {
  padding: '8px 10px 10px',
  borderTop: '1px solid rgba(255, 255, 255, 0.07)',
  fontSize: 12,
  color: '#cbd5e1',
};
const STYLE_HEADER: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  marginBottom: 6,
};
const STYLE_TITLE: React.CSSProperties = {
  fontWeight: 700,
  color: '#a5b4fc',
  fontSize: 11,
  letterSpacing: 0.04,
  textTransform: 'uppercase',
};
const STYLE_PILLS: React.CSSProperties = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: 4,
  margin: '4px 0 8px',
};
const STYLE_PILL: React.CSSProperties = {
  fontSize: 10.5,
  fontWeight: 600,
  background: 'rgba(99, 102, 241, 0.18)',
  color: '#c7d2fe',
  border: '1px solid rgba(99, 102, 241, 0.32)',
  padding: '2px 7px',
  borderRadius: 999,
  whiteSpace: 'nowrap',
};
const STYLE_GROUP_HEADER: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  margin: '8px 0 4px',
  fontSize: 10.5,
  color: '#94a3b8',
  fontWeight: 700,
  textTransform: 'uppercase',
  letterSpacing: 0.04,
};
const STYLE_ROW: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 6,
  padding: '4px 6px',
  borderRadius: 6,
  background: 'rgba(255, 255, 255, 0.03)',
  marginBottom: 3,
};
const STYLE_ROW_NAME: React.CSSProperties = {
  flex: 1,
  minWidth: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  fontWeight: 600,
  color: '#e2e8f0',
};
const STYLE_ICON_BTN: React.CSSProperties = {
  background: 'transparent',
  border: '1px solid rgba(255, 255, 255, 0.10)',
  color: '#cbd5e1',
  borderRadius: 5,
  padding: '2px 6px',
  fontSize: 11,
  cursor: 'pointer',
  flexShrink: 0,
};
const STYLE_LINK_BTN: React.CSSProperties = {
  background: 'transparent',
  border: 'none',
  color: '#8b9bff',
  cursor: 'pointer',
  fontSize: 10.5,
  padding: 0,
  fontWeight: 700,
  textTransform: 'uppercase',
  letterSpacing: 0.04,
};
const STYLE_QUICK_BAR: React.CSSProperties = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: 4,
  marginTop: 8,
};
const STYLE_QUICK_BTN: React.CSSProperties = {
  flex: '0 0 auto',
  background: 'rgba(99, 102, 241, 0.16)',
  color: '#c7d2fe',
  border: '1px solid rgba(99, 102, 241, 0.32)',
  borderRadius: 6,
  padding: '4px 8px',
  fontSize: 11,
  fontWeight: 600,
  cursor: 'pointer',
};
const STYLE_EMPTY: React.CSSProperties = {
  fontSize: 11,
  color: '#94a3b8',
  fontStyle: 'italic',
  margin: '4px 0',
};
const STYLE_ERROR: React.CSSProperties = {
  fontSize: 11,
  color: '#fca5a5',
  margin: '4px 0',
};

// ─── Component ─────────────────────────────────────────────────────────

export const SpatialObjectsCountrySection: React.FC<SpatialObjectsCountrySectionProps> = ({
  scope,
  onOpenAdmin,
  onFlyTo,
  walletAddress,
}) => {
  const canEdit = !!walletAddress;
  const lockTip = canEdit ? '' : 'Connect a wallet to add or edit spatial objects';
  const [inCountry, setInCountry]     = useState<Row[]>([]);
  const [nearby, setNearby]           = useState<Row[]>([]);
  const [loading, setLoading]         = useState(false);
  const [errorMsg, setErrorMsg]       = useState<string | null>(null);
  const [expandCountry, setExpandCountry] = useState(false);
  const [expandNearby, setExpandNearby]   = useState(false);

  const { bbox, centroid, countryName, polygon, mapCenter, mapZoom } = scope;

  // Reference point for "closest first" ordering: whatever the
  // operator is currently looking at (live map center), falling back
  // to the country centroid. As the user zooms/pans, mapCenter
  // updates → the lists re-sort so the nearest objects to the current
  // view float to the top. That's the "start showing the closest
  // ones as I zoom in" behaviour.
  const refLng = mapCenter?.lng ?? centroid?.lng ?? null;
  const refLat = mapCenter?.lat ?? centroid?.lat ?? null;

  // Stable scalars for the refetch effect's dependency array — CI's
  // eslint (`react-hooks/exhaustive-deps`) refuses complex expressions
  // like `bbox?.[0]` directly in `useEffect(..., [...])`, so we hoist
  // them out here.
  const bboxMinLng = bbox?.[0];
  const bboxMinLat = bbox?.[1];
  const bboxMaxLng = bbox?.[2];
  const bboxMaxLat = bbox?.[3];
  const centroidLng = centroid?.lng;
  const centroidLat = centroid?.lat;

  // Refetch whenever the active country changes.
  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      setLoading(true);
      setErrorMsg(null);
      try {
        const promises: Array<Promise<Row[]>> = [];

        // 1. In-country query (bbox). High limit so we can show ALL
        //    objects that fall inside the country polygon, not just a
        //    sample — the user wants "all the UAE ones".
        if (bbox) {
          promises.push(
            geoLinkSpatialObjectsAdminClient.listObjects({
              bbox: {
                sw: { lng: bbox[0], lat: bbox[1] },
                ne: { lng: bbox[2], lat: bbox[3] },
              },
              limit: 500,
            }),
          );
        } else {
          promises.push(Promise.resolve([] as Row[]));
        }

        // 2. Nearby query (centroid + radius).
        if (centroid) {
          promises.push(
            geoLinkSpatialObjectsAdminClient.listObjects({
              near: { lng: centroid.lng, lat: centroid.lat, radius_meters: NEARBY_RADIUS_M },
              limit: 50,
            }),
          );
        } else {
          promises.push(Promise.resolve([] as Row[]));
        }

        const [inCountryRows, nearbyRows] = await Promise.all(promises);
        if (cancelled) return;
        const inCountrySafe = inCountryRows ?? [];
        const inCountryIds  = new Set(inCountrySafe.map((r) => r.id));
        setInCountry(inCountrySafe);
        setNearby(withoutIds(nearbyRows ?? [], inCountryIds));
      } catch (e) {
        if (!cancelled) setErrorMsg((e as Error).message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    void run();
    return () => { cancelled = true; };
    // We only re-run when the actual scope identity changes — the
    // hoisted primitives above (bbox{Min,Max}{Lng,Lat}, centroid{Lng,
    // Lat}) keep CI's exhaustive-deps lint happy without re-running on
    // every parent render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    scope.countryCode,
    scope.countryIso2,
    bboxMinLng, bboxMinLat, bboxMaxLng, bboxMaxLat,
    centroidLng, centroidLat,
  ]);

  // Objects that actually fall inside THIS country's polygon. The
  // bbox query returns a rectangular slab that overlaps neighbours
  // (UAE's bbox clips bits of Oman / Saudi / Iran), so we point-in-
  // polygon filter to keep only the genuine in-country objects. When
  // we have no polygon (geojson still loading) we fall back to the
  // raw bbox rows so the panel isn't empty.
  const inCountryScoped = useMemo(() => {
    if (!polygon) return inCountry;
    return inCountry.filter((r) => {
      const c = proximityRowCenter(r);
      return c ? pointInPolygon(c, polygon) : false;
    });
  }, [inCountry, polygon]);

  // Sort closest-first relative to the current view. Rows without a
  // resolvable centre sink to the bottom (Infinity distance).
  const sortByDistance = useCallback((rows: Row[]): Array<Row & { _distM: number }> => {
    const ref = (refLng != null && refLat != null) ? { lng: refLng, lat: refLat } : null;
    return rows
      .map((r) => {
        const c = proximityRowCenter(r);
        const _distM = (ref && c) ? haversineMeters(ref, c) : Number.POSITIVE_INFINITY;
        return Object.assign({}, r, { _distM });
      })
      .sort((a, b) => a._distM - b._distM);
  }, [refLng, refLat]);

  const inCountrySorted = useMemo(() => sortByDistance(inCountryScoped), [sortByDistance, inCountryScoped]);
  const nearbySorted    = useMemo(() => sortByDistance(nearby),          [sortByDistance, nearby]);

  const counts = useMemo(() => countByType(inCountryScoped), [inCountryScoped]);

  // How many in-country rows to show before "Show all". When the
  // operator is zoomed in (looking at a specific area) we surface a
  // longer closest-first list so the relevant objects are visible
  // without an extra click; zoomed out we keep it compact.
  const initialCount = (mapZoom ?? 0) >= 9 ? 8 : TOP_N;

  const visibleInCountry = useMemo(
    () => (expandCountry ? inCountrySorted : inCountrySorted.slice(0, initialCount)),
    [expandCountry, inCountrySorted, initialCount],
  );
  const visibleNearby = useMemo(
    () => (expandNearby ? nearbySorted : nearbySorted.slice(0, TOP_N)),
    [expandNearby, nearbySorted],
  );

  const handleFly = useCallback((row: Row) => {
    const c = rowCenter(row);
    if (c) onFlyTo(c.lng, c.lat, row.heightMeters ?? null);
  }, [onFlyTo]);

  const handleEdit = useCallback((row: Row) => {
    onOpenAdmin({ objectId: row.id });
  }, [onOpenAdmin]);

  const handleQuickAdd = useCallback((tpl: OpenAdminRequest['template']) => {
    // Prefer the live map center over the country centroid: the operator
    // is almost always looking at the area they want the object placed
    // in, while the country centroid is a continent-scale guess that
    // tends to drop new objects on the wrong side of the world.
    const center = scope.mapCenter ?? scope.centroid ?? undefined;
    onOpenAdmin({
      template: tpl,
      center,
      bbox: bbox ?? undefined,
      flyToCenter: (scope.mapZoom ?? 0) < 14, // only fly when we're not already zoomed in
    });
  }, [onOpenAdmin, scope.mapCenter, scope.centroid, scope.mapZoom, bbox]);

  const openManageAll = useCallback(() => {
    onOpenAdmin({ bbox: bbox ?? undefined });
  }, [onOpenAdmin, bbox]);

  // Helper: render one object row. Accepts the distance-annotated
  // row so it can show a "120 m / 3.4 km" pill when the distance is
  // known (it always is once a reference point + centroid resolve).
  const renderRow = (row: Row & { _distM?: number }) => {
    const meta = TYPE_META[(row.objectType ?? 'object_3d') as SpatialObjectType] ?? { label: 'object', icon: '•' };
    const distLabel = (row._distM != null && Number.isFinite(row._distM)) ? formatDistance(row._distM) : null;
    return (
      <div key={row.id} style={STYLE_ROW}>
        <span title={meta.label} style={{ fontSize: 13, flexShrink: 0 }}>{meta.icon}</span>
        <span style={STYLE_ROW_NAME} title={`${meta.label} · ${row.id}`}>
          {row.name || row.id.slice(0, 8)}
        </span>
        {distLabel && (
          <span style={{ fontSize: 10, color: '#94a3b8', fontVariantNumeric: 'tabular-nums', flexShrink: 0 }}>
            {distLabel}
          </span>
        )}
        {row.linkedExecutionRuleId != null && (
          <span style={{ ...STYLE_PILL, fontSize: 9, padding: '1px 5px' }} title={`Linked rule #${row.linkedExecutionRuleId}`}>
            ⚡#{String(row.linkedExecutionRuleId)}
          </span>
        )}
        <button
          type="button"
          style={STYLE_ICON_BTN}
          title="Fly to object"
          onClick={(e) => { e.stopPropagation(); handleFly(row); }}
        >📍</button>
        <button
          type="button"
          style={{
            ...STYLE_ICON_BTN,
            opacity: canEdit ? 1 : 0.4,
            cursor: canEdit ? 'pointer' : 'not-allowed',
          }}
          title={canEdit ? 'Edit in admin panel' : lockTip}
          disabled={!canEdit}
          onClick={(e) => { e.stopPropagation(); if (canEdit) handleEdit(row); }}
        >✎</button>
      </div>
    );
  };

  // Stop the country tooltip from dismissing when the user clicks
  // inside the spatial section. The desktop tooltip dismisses on
  // mouse-leave, but clicks inside should never bubble up to the
  // map's country-click handler.
  const stop = (e: React.SyntheticEvent) => e.stopPropagation();

  return (
    <div style={STYLE_SECTION} onClick={stop}>
      <div style={STYLE_HEADER}>
        <div style={STYLE_TITLE}>🏙️ Spatial objects</div>
        <button
          type="button"
          style={STYLE_LINK_BTN}
          title="Open the spatial-objects admin panel filtered to this country"
          onClick={openManageAll}
        >
          Manage →
        </button>
      </div>

      {loading && <div style={STYLE_EMPTY}>Loading…</div>}
      {errorMsg && <div style={STYLE_ERROR}>{errorMsg}</div>}

      {!loading && !errorMsg && counts.length > 0 && (
        <div style={STYLE_PILLS}>
          {counts.map(({ type, n }) => (
            <span key={type} style={STYLE_PILL}>
              {TYPE_META[type]?.icon ?? '•'} {n} {TYPE_META[type]?.label ?? type}
            </span>
          ))}
        </div>
      )}

      {!loading && !errorMsg && (
        <>
          {/* In-country list — every object inside the country
              polygon, sorted closest-first to the current view. */}
          {bbox && (
            <>
              <div style={STYLE_GROUP_HEADER}>
                <span>In {countryName} ({inCountryScoped.length})</span>
                {inCountryScoped.length > initialCount && (
                  <button
                    type="button"
                    style={STYLE_LINK_BTN}
                    onClick={() => setExpandCountry((v) => !v)}
                  >
                    {expandCountry ? 'Show less' : `Show all ${inCountryScoped.length}`}
                  </button>
                )}
              </div>
              {visibleInCountry.length === 0
                ? <div style={STYLE_EMPTY}>No spatial objects yet in this country.</div>
                : visibleInCountry.map(renderRow)}
            </>
          )}

          {/* Nearby list — objects outside the country polygon but
              within range, also closest-first. */}
          {nearbySorted.length > 0 && (
            <>
              <div style={STYLE_GROUP_HEADER}>
                <span>Nearby ({nearbySorted.length})</span>
                {nearbySorted.length > TOP_N && (
                  <button
                    type="button"
                    style={STYLE_LINK_BTN}
                    onClick={() => setExpandNearby((v) => !v)}
                  >
                    {expandNearby ? 'Show less' : `Show all ${nearbySorted.length}`}
                  </button>
                )}
              </div>
              {visibleNearby.map(renderRow)}
            </>
          )}
        </>
      )}

      {/* Quick-add row — opens the floating admin panel pre-filled.
          Disabled when the wallet is not connected since GeoLink rejects
          unauthenticated writes; the admin panel re-enforces the same
          guard at save time. */}
      <div style={STYLE_QUICK_BAR}>
        {QUICK_TEMPLATES.map((t) => (
          <button
            key={t.id}
            type="button"
            style={{
              ...STYLE_QUICK_BTN,
              opacity: canEdit ? 1 : 0.45,
              cursor: canEdit ? 'pointer' : 'not-allowed',
            }}
            title={canEdit
              ? `Create a ${t.label.replace('+ ', '').toLowerCase()} centred on ${countryName}`
              : lockTip}
            disabled={!canEdit}
            onClick={() => { if (canEdit) handleQuickAdd(t.id); }}
          >
            {t.icon} {t.label}
          </button>
        ))}
      </div>
      {!canEdit && (
        <div style={{ fontSize: 10, color: '#9ca3af', marginTop: 4, textAlign: 'center' }}>
          Connect a wallet to add or edit spatial objects.
        </div>
      )}
    </div>
  );
};

export default SpatialObjectsCountrySection;
